/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useEffect, useRef } from "react";
import $ from "jquery";
import "bootstrap";
import "summernote/dist/summernote-lite.css";
import "summernote/dist/summernote-lite.js";

declare global {
  interface Window {
    $: any;
    jQuery: any;
  }
}

if (typeof window !== "undefined") {
  window.$ = window.jQuery = $;
}

export default function SummernoteEditor({
  initialContent,
  onChange,
  editorId,
}: {
  initialContent: string;
  onChange: (content: string) => void;
  editorId: string;
}) {
  const editorRef = useRef<HTMLTextAreaElement | null>(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current && editorRef.current) {
      initialized.current = true;

      const $editor = $(editorRef.current);

      /* ---------------------- INSERT CODE BLOCK ---------------------- */
      const insertCodeBlock = () => {
        const codeBlock = `
          <div class="code-wrapper mt-4">
            <div class="relative bg-gray-100 border rounded-lg p-4 font-mono overflow-auto">
              <button type="button" class="copy-btn absolute top-2 right-2 bg-blue-600 text-white px-3 py-1 rounded">
                📋 Copy
              </button>
              <pre><code class="language-javascript">console.log("Hello, world!");</code></pre>
            </div>
          </div>
        `;
        ($editor as any).summernote("pasteHTML", codeBlock);
      };

      /* ---------------------- FORMAT TEXT DROPDOWN ---------------------- */
      const formatTextDropdown = () => {
        const dropdown = $(`
          <div class="format-dropdown-container inline-block relative">
            <button id="dropdownButton-${editorId}" class="btn btn-sm bg-gray-700 text-white px-3 py-1 rounded">
              Text Format ▼
            </button>

            <ul id="dropdownMenu-${editorId}" class="hidden bg-white border rounded shadow-md mt-1 absolute z-50 w-40">
              <li><a href="#" class="block px-4 py-1" data-tag="p">Paragraph</a></li>
              <li><a href="#" class="block px-4 py-1" data-tag="h1">Heading 1</a></li>
              <li><a href="#" class="block px-4 py-1" data-tag="h2">Heading 2</a></li>
              <li><a href="#" class="block px-4 py-1" data-tag="h3">Heading 3</a></li>
              <li><a href="#" class="block px-4 py-1" data-tag="h4">Heading 4</a></li>
              <li><a href="#" class="block px-4 py-1" data-tag="h5">Heading 5</a></li>
              <li><a href="#" class="block px-4 py-1" data-tag="h6">Heading 6</a></li>
            </ul>
          </div>
        `);

        dropdown.find(`#dropdownButton-${editorId}`).on("click", () => {
          dropdown.find(`#dropdownMenu-${editorId}`).toggleClass("hidden");
        });

        dropdown.find("a").on("click", function (e) {
          e.preventDefault();
          const tag = $(this).data("tag");

          const sel = window.getSelection();
          if (sel && sel.rangeCount > 0) {
            const range = sel.getRangeAt(0);
            if (!range.collapsed) {
              const wrapper = document.createElement(tag);
              wrapper.appendChild(range.extractContents());
              range.insertNode(wrapper);
            }
          }

          dropdown.find(`#dropdownMenu-${editorId}`).addClass("hidden");
        });

        $(document).on("click", (e) => {
          if (!dropdown[0].contains(e.target)) {
            dropdown.find(`#dropdownMenu-${editorId}`).addClass("hidden");
          }
        });

        return dropdown;
      };

      /* ---------------------- SUMMERNOTE INIT ---------------------- */
      ($editor as any).summernote({
        height: 350,
        placeholder: "Write your content...",

        fontNames: [
          "Arial",
          "Helvetica",
          "Times New Roman",
          "Courier New",
          "Comic Sans MS",
          "Georgia",
          "Verdana",
          "Roboto",
          "Inter",
          "Poppins",
        ],

        toolbar: [
          ["style", ["bold", "italic", "underline", "strikethrough", "clear"]],
          ["font", ["fontname", "fontsize"]],
          ["color", ["color"]],        // TEXT COLOR
          ["background", ["backcolor"]], // BACKGROUND COLOR (SEPARATE BUTTON)
          ["para", ["ul", "ol", "paragraph"]],
          ["insert", ["link", "picture", "video", "table"]],
          ["custom", ["insertCode", "formatText"]],
          ["history", ["undo", "redo"]],
          ["view", ["fullscreen", "codeview", "help"]],
        ],

        buttons: {
          insertCode: function () {
            const btn = $(
              '<button type="button" class="btn btn-sm border bg-red-200 px-3 py-1 rounded">&lt;/&gt; Code</button>'
            );
            btn.on("click", insertCodeBlock);
            return btn;
          },
          formatText: formatTextDropdown,
        },

        callbacks: {
          onChange: function (contents: string) {
            onChange(contents);
          },
        },
      });

      ($editor as any).summernote("code", initialContent);

      /* ---------------------- COPY BUTTON ---------------------- */
      const handleCopyClick = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        if (target.classList.contains("copy-btn")) {
          const code = target.closest(".code-wrapper")?.querySelector("code");
          if (code) {
            navigator.clipboard.writeText(code.textContent ?? "");
            target.textContent = "✅ Copied!";
            setTimeout(() => (target.textContent = "📋 Copy"), 2000);
          }
        }
      };

      document.addEventListener("click", handleCopyClick);
      return () => document.removeEventListener("click", handleCopyClick);
    }
  }, [initialContent, onChange, editorId]);

  return <textarea ref={editorRef}></textarea>;
}
